import React, { useState, useEffect } from 'react';
import {
  Layers,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Users,
  Clock,
  PhoneCall,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { callQueuesAPI, extensionsAPI } from '../services/api';

const CallQueues = () => {
  const [callQueues, setCallQueues] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [newQueue, setNewQueue] = useState({
    name: '',
    extension: '',
    agents: [],
    max_wait: 300,
    strategy: 'longest-idle',
    music_on_hold: 'default',
    announce_position: true,
    announce_wait: true,
    status: 'active'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [queuesRes, extsRes] = await Promise.all([
        callQueuesAPI.list(),
        extensionsAPI.list()
      ]);
      setCallQueues(queuesRes.data);
      setExtensions(extsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredQueues = callQueues.filter((queue) =>
    queue.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    queue.extension.includes(searchQuery)
  );

  const getStrategyLabel = (strategy) => {
    const strategies = {
      'longest-idle': 'Longest Idle',
      'round-robin': 'Round Robin',
      'ring-all': 'Ring All',
      'skill-based': 'Skill Based',
      'random': 'Random'
    };
    return strategies[strategy] || strategy;
  };

  const getMemberName = (memberId) => {
    const ext = extensions.find((e) => e.id === memberId);
    return ext ? ext.name : memberId;
  };

  const handleAgentToggle = (agentId, isNew = true) => {
    if (isNew) {
      if (newQueue.agents.includes(agentId)) {
        setNewQueue({ ...newQueue, agents: newQueue.agents.filter((a) => a !== agentId) });
      } else {
        setNewQueue({ ...newQueue, agents: [...newQueue.agents, agentId] });
      }
    } else {
      if (selectedQueue.agents.includes(agentId)) {
        setSelectedQueue({ ...selectedQueue, agents: selectedQueue.agents.filter((a) => a !== agentId) });
      } else {
        setSelectedQueue({ ...selectedQueue, agents: [...selectedQueue.agents, agentId] });
      }
    }
  };

  const handleAddQueue = async () => {
    try {
      await callQueuesAPI.create(newQueue);
      setIsAddModalOpen(false);
      setNewQueue({
        name: '',
        extension: '',
        agents: [],
        max_wait: 300,
        strategy: 'longest-idle',
        music_on_hold: 'default',
        announce_position: true,
        announce_wait: true,
        status: 'active'
      });
      fetchData();
    } catch (error) {
      console.error('Error creating queue:', error);
    }
  };

  const handleEditQueue = async () => {
    try {
      await callQueuesAPI.update(selectedQueue.id, {
        name: selectedQueue.name,
        agents: selectedQueue.agents,
        max_wait: selectedQueue.max_wait,
        strategy: selectedQueue.strategy,
        music_on_hold: selectedQueue.music_on_hold,
        announce_position: selectedQueue.announce_position,
        announce_wait: selectedQueue.announce_wait,
        status: selectedQueue.status
      });
      setIsEditModalOpen(false);
      setSelectedQueue(null);
      fetchData();
    } catch (error) {
      console.error('Error updating queue:', error);
    }
  };

  const handleDeleteQueue = async (id) => {
    try {
      await callQueuesAPI.delete(id);
      fetchData();
    } catch (error) {
      console.error('Error deleting queue:', error);
    }
  };

  const userExtensions = extensions.filter((ext) => ext.type === 'user');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Call Queues</h1>
          <p className="text-gray-400 mt-1">Manage call queues and agent distribution</p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Queue
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Queues</p>
                <p className="text-2xl font-bold text-white">{callQueues.length}</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Layers className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Active Calls</p>
                <p className="text-2xl font-bold text-green-400">
                  {callQueues.reduce((acc, q) => acc + (q.current_calls || 0), 0)}
                </p>
              </div>
              <div className="p-3 bg-green-500/20 rounded-lg">
                <PhoneCall className="w-5 h-5 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Waiting Calls</p>
                <p className="text-2xl font-bold text-yellow-400">
                  {callQueues.reduce((acc, q) => acc + (q.waiting_calls || 0), 0)}
                </p>
              </div>
              <div className="p-3 bg-yellow-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#12121a] border-[#1e1e2d]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Agents</p>
                <p className="text-2xl font-bold text-purple-400">
                  {callQueues.reduce((acc, q) => acc + q.agents.length, 0)}
                </p>
              </div>
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="bg-[#12121a] border-[#1e1e2d]">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search call queues..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1e1e2d] border-[#2a2a3d] text-white placeholder-gray-500"
              />
            </div>
            <Button variant="ghost" className="text-gray-400 hover:text-white" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Queues List */}
      <div className="grid gap-4">
        {filteredQueues.map((queue) => (
          <Card key={queue.id} className="bg-[#12121a] border-[#1e1e2d] hover:border-[#2a2a3d] transition-colors">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-cyan-500/20 rounded-lg">
                    <Layers className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-white">{queue.name}</h3>
                      <Badge className={queue.status === 'active'
                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                        : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                      }>
                        {queue.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-400">Extension: {queue.extension}</p>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-400">{queue.current_calls || 0}</p>
                    <p className="text-xs text-gray-500">Active</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-yellow-400">{queue.waiting_calls || 0}</p>
                    <p className="text-xs text-gray-500">Waiting</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{queue.avg_wait_time || '0:00'}</p>
                    <p className="text-xs text-gray-500">Avg Wait</p>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-white">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#1e1e2d] border-[#2a2a3d]">
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedQueue(queue);
                          setIsEditModalOpen(true);
                        }}
                        className="text-gray-300 hover:text-white hover:bg-[#2a2a3d] cursor-pointer"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDeleteQueue(queue.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20 cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-[#1e1e2d] rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Strategy</p>
                  <p className="text-sm text-white">{getStrategyLabel(queue.strategy)}</p>
                </div>
                <div className="bg-[#1e1e2d] rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Max Wait</p>
                  <p className="text-sm text-white">{Math.floor(queue.max_wait / 60)}m {queue.max_wait % 60}s</p>
                </div>
                <div className="bg-[#1e1e2d] rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Music on Hold</p>
                  <p className="text-sm text-white capitalize">{queue.music_on_hold}</p>
                </div>
                <div className="bg-[#1e1e2d] rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Agents</p>
                  <p className="text-sm text-white">{queue.agents.length} assigned</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-[#1e1e2d]">
                <p className="text-xs text-gray-500 mb-2">Agents</p>
                <div className="flex flex-wrap gap-2">
                  {queue.agents.map((agentId) => (
                    <Badge key={agentId} variant="secondary" className="bg-[#1e1e2d] text-white">
                      {getMemberName(agentId)} ({agentId})
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredQueues.length === 0 && (
          <Card className="bg-[#12121a] border-[#1e1e2d]">
            <CardContent className="p-12 text-center">
              <Layers className="w-12 h-12 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No call queues found</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Queue Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="bg-[#12121a] border-[#1e1e2d] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Add Call Queue</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Queue Name</Label>
                <Input
                  value={newQueue.name}
                  onChange={(e) => setNewQueue({ ...newQueue, name: e.target.value })}
                  placeholder="Customer Support"
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Extension</Label>
                <Input
                  value={newQueue.extension}
                  onChange={(e) => setNewQueue({ ...newQueue, extension: e.target.value })}
                  placeholder="700"
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Strategy</Label>
                <Select
                  value={newQueue.strategy}
                  onValueChange={(value) => setNewQueue({ ...newQueue, strategy: value })}
                >
                  <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                    <SelectItem value="longest-idle">Longest Idle</SelectItem>
                    <SelectItem value="round-robin">Round Robin</SelectItem>
                    <SelectItem value="ring-all">Ring All</SelectItem>
                    <SelectItem value="skill-based">Skill Based</SelectItem>
                    <SelectItem value="random">Random</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Max Wait (seconds)</Label>
                <Input
                  type="number"
                  value={newQueue.max_wait}
                  onChange={(e) => setNewQueue({ ...newQueue, max_wait: parseInt(e.target.value) })}
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">Music on Hold</Label>
              <Select
                value={newQueue.music_on_hold}
                onValueChange={(value) => setNewQueue({ ...newQueue, music_on_hold: value })}
              >
                <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="jazz">Jazz</SelectItem>
                  <SelectItem value="classical">Classical</SelectItem>
                  <SelectItem value="pop">Pop</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-gray-400">Announce Position</Label>
              <Switch
                checked={newQueue.announce_position}
                onCheckedChange={(checked) => setNewQueue({ ...newQueue, announce_position: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-gray-400">Announce Wait Time</Label>
              <Switch
                checked={newQueue.announce_wait}
                onCheckedChange={(checked) => setNewQueue({ ...newQueue, announce_wait: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">Agents</Label>
              <div className="bg-[#1e1e2d] border border-[#2a2a3d] rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
                {userExtensions.map((ext) => (
                  <div key={ext.id} className="flex items-center space-x-3">
                    <Checkbox
                      id={`new-agent-${ext.id}`}
                      checked={newQueue.agents.includes(ext.id)}
                      onCheckedChange={() => handleAgentToggle(ext.id, true)}
                    />
                    <label htmlFor={`new-agent-${ext.id}`} className="text-sm text-white cursor-pointer">
                      {ext.name} ({ext.id})
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddModalOpen(false)} className="border-[#2a2a3d] text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleAddQueue} className="bg-blue-600 hover:bg-blue-700">
              Add Queue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Queue Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="bg-[#12121a] border-[#1e1e2d] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Edit Call Queue</DialogTitle>
          </DialogHeader>
          {selectedQueue && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Queue Name</Label>
                  <Input
                    value={selectedQueue.name}
                    onChange={(e) => setSelectedQueue({ ...selectedQueue, name: e.target.value })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Extension</Label>
                  <Input
                    value={selectedQueue.extension}
                    disabled
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white opacity-50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Strategy</Label>
                  <Select
                    value={selectedQueue.strategy}
                    onValueChange={(value) => setSelectedQueue({ ...selectedQueue, strategy: value })}
                  >
                    <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                      <SelectItem value="longest-idle">Longest Idle</SelectItem>
                      <SelectItem value="round-robin">Round Robin</SelectItem>
                      <SelectItem value="ring-all">Ring All</SelectItem>
                      <SelectItem value="skill-based">Skill Based</SelectItem>
                      <SelectItem value="random">Random</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Max Wait (seconds)</Label>
                  <Input
                    type="number"
                    value={selectedQueue.max_wait}
                    onChange={(e) => setSelectedQueue({ ...selectedQueue, max_wait: parseInt(e.target.value) })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400">Status</Label>
                <Select
                  value={selectedQueue.status}
                  onValueChange={(value) => setSelectedQueue({ ...selectedQueue, status: value })}
                >
                  <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-gray-400">Announce Position</Label>
                <Switch
                  checked={selectedQueue.announce_position}
                  onCheckedChange={(checked) => setSelectedQueue({ ...selectedQueue, announce_position: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-gray-400">Announce Wait Time</Label>
                <Switch
                  checked={selectedQueue.announce_wait}
                  onCheckedChange={(checked) => setSelectedQueue({ ...selectedQueue, announce_wait: checked })}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400">Agents</Label>
                <div className="bg-[#1e1e2d] border border-[#2a2a3d] rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
                  {userExtensions.map((ext) => (
                    <div key={ext.id} className="flex items-center space-x-3">
                      <Checkbox
                        id={`edit-agent-${ext.id}`}
                        checked={selectedQueue.agents.includes(ext.id)}
                        onCheckedChange={() => handleAgentToggle(ext.id, false)}
                      />
                      <label htmlFor={`edit-agent-${ext.id}`} className="text-sm text-white cursor-pointer">
                        {ext.name} ({ext.id})
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)} className="border-[#2a2a3d] text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleEditQueue} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CallQueues;
